import { Dialog, DialogClose, DialogTitle } from "@suse-o11y/dialog";
import { IconClose } from "@suse-o11y/icons";
import type { Record } from "@suse-o11y/record";
import { useComputed } from "@suse-o11y/signal";
import { usePermanent } from "@suse-o11y/use-permanent";
import React from "react";

import { Alert } from "~/components/alert";

import { DocLink } from "../react/docLink/DocLink";

import { type InitParams, init as initFormState } from "./_form-state";
import { SetupDashboardForm } from "./_setup-dashboard-form";
import type { SetupDashboardDrawerVariant } from "./variant";

const STATE_TYPE_TO_TITLE = {
	create: "Add New Dashboard",
	edit: "Edit Dashboard Properties",
	clone: "Save Dashboard As...",
} satisfies Record<SetupDashboardDrawerVariant["type"]>;

const STATE_TYPE_TO_BUTTON_TEXT = {
	create: "Create dashboard",
	edit: "Update dashboard",
	clone: "Create dashboard",
} satisfies Record<SetupDashboardDrawerVariant["type"], string>;

const FORM_ID = "setup-dashboard";

type SetupDashboardDrawerProps = SetupDashboardDrawerVariant & {
	initial: InitParams;
};

export const SetupDashboardDrawer: React.FC<SetupDashboardDrawerProps> = (props) => (
	<Dialog className="drawer/dialog drawer-narrow">
		<SetupDashboardContent {...props} />
	</Dialog>
);

const SetupDashboardContent: React.FC<SetupDashboardDrawerProps> = ({ initial, ...variant }) => {
	const form = usePermanent(() => initFormState(initial));
	const isSubmitting = useComputed((monitor) => form.isSubmitting(monitor), [form]);

	return (
		<>
			<header className="drawer-header">
				<DialogTitle className="drawer-title">{STATE_TYPE_TO_TITLE[variant.type]}</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<section className="drawer-body">
				{variant.type === "edit" ? (
					<SetupDashboardAlert>You are about to update the properties of the current dashboard.</SetupDashboardAlert>
				) : (
					<SetupDashboardAlert>
						All the dashboards can be found under the <strong className="font-semibold">Dashboards</strong> main menu
						entry.
					</SetupDashboardAlert>
				)}

				<SetupDashboardForm form={form} formId={FORM_ID} variant={variant} />
			</section>

			<footer className="drawer-footer">
				<DialogClose className="button button-tertiary uppercase">Close</DialogClose>

				{(variant.type !== "edit" || !variant.readonly) && (
					<button aria-busy={isSubmitting} className="button button-primary uppercase" form={FORM_ID} type="submit">
						{STATE_TYPE_TO_BUTTON_TEXT[variant.type]}
					</button>
				)}
			</footer>
		</>
	);
};

const SetupDashboardAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<Alert className="mb-4">
		{children} To learn more about dashboards, check out{" "}
		<DocLink capture="noop" slug="ui-dashboards">
			our docs
		</DocLink>
		.
	</Alert>
);
